import { requireInventoryFloor } from '../../compile/inventory-facet';
// janus-engine/ability/aoe/expand-self-follow-aura.ts — the browser twin of validate_and_adjust_targets.php ~L325-347 for a Self-range
// creature-following AURA (aoe_type + aoe_distance + persistent_aoe + aoe_follows_target) on the initial
// cast (Session 1 step 2, 2026-09-29 — Heat Vent):
//   · the selection is REPLACED by the caster (target_id = caster);
//   · expand_self_follow_aura_targets_if_needed() (aoe_helpers.php) then appends, in ROSTER order, every other creature whose footprint
//     the aura reaches — the centered stub janus_build_centered_aoe_status_stub_for_hit_test() at the caster, judged by
//     persistent_aoe_depth_bearing_intersection() (floor-plane x/y + the elevation band, via zone-membership.ts zoneContains) — that
//     survives the inventory-only / team-type / opposing-only / friendly-only filters.
// Every creature uses the same drawn ground-plane body, including rows that omit a zero y_pos.
import { type JanusCtx, type Row, ecId, ecLean, inventoryId, itemField, handBack, record, upper } from '../context.js';
import { checkOpposingTeam } from '../check-opposing-team.js';
import { castSkipsSameTeam, sameTeamOrSelf } from '../team-skip-gate.js';
import { zoneContains, ZoneHandBack, type Zone } from '../../zones/zone-membership.js';
import { SHAPES, selfAoeDistance } from './expand-placed-aoe-targets.js';
import { isAuraEffect } from './aura-classification.js';
export { isAuraEffect } from './aura-classification.js';

const SELF = 'D82855E1-4BB9-93EA-6374-B9904318BC1D';
/** PHP empty(). */
const filled = (value: unknown): boolean => value != null && value !== '' && value !== 0 && value !== '0' && value !== false;

/** The Self-range aura whose initial cast replaces the selection with the caster. */
export function isSelfFollowAura(ctx: JanusCtx, ability: Row): boolean {
  return isAuraEffect(ctx, ability) && upper(ctx.read.value(ability, 'range')) === SELF;
}

/** pos_coord(): the position container only; a non-numeric / absent key is null. */
function posCoord(row: Row, key: 'x_pos' | 'y_pos' | 'z_pos'): number | null {
  const container = record(row.position) ?? record(row.pos_arr) ?? (('x_pos' in row || 'z_pos' in row || 'y_pos' in row) ? row : null);
  if (!container || !(key in container)) return null;
  let value = container[key];
  while (Array.isArray(value)) value = value[0];
  const n = typeof value === 'number' ? value : typeof value === 'string' && value.trim() !== '' ? Number(value) : NaN;
  return Number.isFinite(n) ? n : null;
}
/** encounter_row_team_uuid(). */
const teamOf = (row: Row): string => { const raw = ecLean(row, 'team'); return upper(record(raw)?.items ? record(record(raw)?.items)?.item_id : raw); };

/** The initial-cast target list: [caster, …every other creature the aura reaches]. */
export function expandSelfFollowAuraTargets(ctx: JanusCtx, ability: Row, caster: Row): Row[] {
  requireInventoryFloor(ctx.encounter);
  const targets: Row[] = [caster];
  const radius = selfAoeDistance(ctx.read, caster, ability, SHAPES[upper(ctx.read.value(ability, 'aoe_type'))] ?? '',
    Number(ctx.read.value(ability, 'aoe_distance') ?? 0));
  if (!Number.isFinite(radius)) handBack('expand_self_follow_aura_targets_if_needed:invalid-distance');
  if (radius <= 0) return targets;
  const roster = Array.isArray(ctx.encounter.encounter_creatures_arr) ? ctx.encounter.encounter_creatures_arr as unknown[] : null;
  if (!roster) handBack('expand_self_follow_aura_targets_if_needed:roster-not-held');
  const cx = posCoord(caster, 'x_pos') ?? 0, cz = posCoord(caster, 'z_pos') ?? 0, cy = posCoord(caster, 'y_pos') ?? 0;
  const heightLayer = Number(ctx.read.value(ability, 'aoe_distance_height') ?? 0);
  const depthLayer = Number(ctx.read.value(ability, 'aoe_distance_depth') ?? 0);
  const zone: Zone = {
    status: { status_aoe_type: upper(ctx.read.value(ability, 'aoe_type')), status_origin_point_x: cx, status_origin_point_z: cz,
      status_distance_point_x: cx, status_distance_point_z: cz, status_aoe_distance: radius,
      status_aoe_distance_height: Number.isFinite(heightLayer) ? heightLayer : 0, status_aoe_distance_depth: Number.isFinite(depthLayer) ? depthLayer : 0,
      ft3d_depth_bearing: true },
    key: '', dynamic: [cx, cy, cz], holderId: ecId(caster),
  };
  const inside = (row: Row): boolean => {
    if (!ctx.read.auraOccupancy) handBack('expand_self_follow_aura_targets_if_needed:occupancy-reader-not-held');
    const occupancy = ctx.read.auraOccupancy!(row);
    if (occupancy === undefined) handBack('expand_self_follow_aura_targets_if_needed:occupancy-not-held');
    if (occupancy === null) return false;
    try { return zoneContains(zone, occupancy); }
    catch (error) { if (error instanceof ZoneHandBack) handBack(`expand_self_follow_aura_targets_if_needed:${error.reason}`); throw error; }
  };
  const casterId = ecId(caster);
  const seen = new Set([casterId]);
  for (const entry of roster!) {
    const listed = record(entry);
    const id = ecId(listed);
    if (!listed || !id || seen.has(id)) continue;
    const creature = ctx.members.get(id) ?? listed;
    if (!inside(creature)) continue;
    // janus.php's PRIVATE-MONSTER TARGET FILTER (~L1035, right after this expansion): a private creature (a lair, a GM-hidden actor) is
    // never another creature's target — dropped here, before any dispatch, exactly as PHP strips it (Codex review 2026-09-29).
    const privateFlag = ecLean(creature, 'encounter_creature_private');
    if (privateFlag == null) handBack('expand_self_follow_aura_targets_if_needed:private-flag-not-held');
    if (filled(privateFlag)) continue;
    if (filled(ctx.read.value(ability, 'ability_only_affects_inventory'))) continue;
    const opposing = checkOpposingTeam(ctx, caster, creature);
    // persistent_aoe_team_type_allows_target() (no zone row on the initial cast)
    if (filled(ctx.read.value(ability, 'effect_only_affects_opposing_team_type')) && !opposing) continue;
    if (filled(ctx.read.value(ability, 'effect_only_affects_same_team_type')) && opposing) continue;
    if (filled(ctx.read.value(ability, 'target_opposing_team_only')) && !opposing) continue;
    if (filled(ctx.read.value(ability, 'target_friendly_only')) && teamOf(creature) !== teamOf(caster)) continue;
    // Wizard v9 aoe_spells_spare_allies (caster-spell-layers.ts): the caster's own team is skipped; neutrals still join
    if (castSkipsSameTeam(ability) && sameTeamOrSelf(caster, creature, (row, key) => ctx.read.value(row, key))) continue;
    targets.push(creature);
    seen.add(id);
  }
  // The PHP inventory sweep follows the creature sweep; props have no opposing team.
  const inventories = Array.isArray(ctx.encounter.encounter_inventories_arr) ? ctx.encounter.encounter_inventories_arr as unknown[] : [];
  if (inventories.length && !ctx.read.has(ability, 'ability_does_not_effect_inventory')
      && !filled(ctx.read.value(ability, 'effect_only_affects_opposing_team_type')) && !filled(ctx.read.value(ability, 'target_opposing_team_only')))
    for (const entry of inventories) {
      const prop = record(entry), id = inventoryId(prop);
      if (prop && id && !seen.has(id) && itemField(prop, 'item_type') !== 'map_marker' && inside(prop)) {
        targets.push(prop); seen.add(id);
      }
    }
  return targets;
}
